import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { asArray } from '@/lib/utils';
import ProductSlider from '@/components/ProductSlider';
import RecommendedSection from '@/components/RecommendedSection';
import { getRecentlyViewed, onRecentlyViewedChange } from '@/lib/recentlyViewed';
import { Skeleton } from '@/components/ui/skeleton';
import { useSettings } from '@/contexts/SettingsContext';
import { DELIVERY_FEE } from '@/constants/shop';
import HeroCarousel from '@/components/home/HeroCarousel';
import { TrustBar, CategoryTiles, DealOfTheDay, PromoBanners, WhatsAppCta, SectionHeading } from '@/components/home/HomeSections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// "وصل حديثاً" = المنتجات المضافة خلال آخر 30 يوم
const NEW_DAYS = 30;

// هيكل تحميل بنفس شكل قسم المنتجات (العنوان يمين والزر شمال + شبكة المنتجات)
const CategorySectionSkeleton = ({ bgClass = 'bg-brand-white' }) => (
  <section className={`py-16 ${bgClass}`} aria-busy="true" aria-label="جاري التحميل">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1600px]">
      <div className="flex items-center justify-between gap-4 mb-8 md:mb-12">
        <Skeleton className="h-8 md:h-10 w-40 md:w-56" />
        <Skeleton className="h-9 w-28 rounded-full" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-8">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className={`rounded-2xl border border-gray-200/80 bg-white overflow-hidden ${i >= 4 ? 'hidden md:block' : ''}`}>
            <Skeleton className="aspect-[3/4] w-full rounded-none" />
            <div className="p-3 md:p-4 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-5 w-1/3" />
              <div className="flex items-center justify-between pt-2">
                <Skeleton className="h-5 w-20 rounded-full" />
                <Skeleton className="h-9 w-9 rounded-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => {
  const site = useSettings();
  const [homeCategories, setHomeCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [saleProducts, setSaleProducts] = useState([]);
  const [saleCount, setSaleCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [recentIds, setRecentIds] = useState(getRecentlyViewed);
  useEffect(() => onRecentlyViewedChange(() => setRecentIds(getRecentlyViewed())), []);

  useEffect(() => {
    fetchHomeCategories();

    // الأقسام + عدد منتجات كل قسم (لكروت "تسوّقي حسب القسم")
    axios
      .get(`${API}/products/filters`)
      .then((res) => setAllCategories(asArray(res.data?.data?.categories)))
      .catch((err) => console.error('Failed to fetch categories:', err));

    fetchOffers();

    // وصل حديثاً: المنتجات المضافة خلال آخر NEW_DAYS يوم
    axios
      .get(`${API}/products`, { params: { new_days: NEW_DAYS, per_page: 1 } })
      .then((res) => setNewCount(res.data?.data?.meta?.total || 0))
      .catch(() => {});
  }, []);

  // أعلى الخصومات: عرض مميز + سلايدر الخصومات + أرقام البانرات
  // (بيتنادى تاني لما عدّاد عرض ينتهي، عشان يختفي/يتبدل بعرض تاني)
  const fetchOffers = () =>
    axios
      .get(`${API}/products`, { params: { on_sale: 1, sort: 'discount', per_page: 12 } })
      .then((res) => {
        setSaleProducts(asArray(res.data?.data?.products));
        setSaleCount(res.data?.data?.meta?.total || 0);
      })
      .catch((err) => console.error('Failed to fetch offers:', err));

  // الأقسام المفعّل لها "الظهور في الرئيسية" مع منتجات عشوائية من كل قسم
  const fetchHomeCategories = async () => {
    setCategoriesLoading(true);
    setCategoriesError(false);
    try {
      const response = await axios.get(`${API}/home/categories`);
      setHomeCategories(asArray(response.data?.data));
    } catch (error) {
      console.error('Failed to fetch home categories:', error);
      setCategoriesError(true);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const maxDiscount = saleProducts[0]?.discount_percentage || 0;
  const deal = saleProducts[0];

  // صورة لكل قسم من منتجاته (لو فيه صور)
  const categoryImages = useMemo(() => {
    const map = {};
    homeCategories.forEach((c) => {
      const withImage = c.products?.find((p) => p.images?.[0]?.url);
      if (withImage) map[c.id] = withImage.images[0].url;
    });
    return map;
  }, [homeCategories]);

  const CategorySection = ({ title, products, categoryValue, bgClass = 'bg-brand-white', eyebrow }) => {
    if (!products || products.length === 0) return null;
    return (
      <section className={`py-12 md:py-16 ${bgClass}`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1600px]">
          <SectionHeading
            eyebrow={eyebrow}
            title={title}
            action={
              <Link to={categoryValue ? `/products?categories=${categoryValue}` : '/products?on_sale=1&sort=discount'} className="shrink-0">
                <Button
                  variant="outline"
                  className="border-burgundy-500 text-burgundy-500 hover:bg-burgundy-500 hover:text-white rounded-full font-cairo"
                >
                  عرض المزيد
                  <ChevronLeft className="mr-1 w-4 h-4" />
                </Button>
              </Link>
            }
          />
          <ProductSlider products={products} />
        </div>
      </section>
    );
  };

  return (
    <div className="animate-fade-up">
      <HeroCarousel siteName={site.site_name} maxDiscount={maxDiscount} saleCount={saleCount} deliveryFee={DELIVERY_FEE} />

      <TrustBar deliveryFee={DELIVERY_FEE} />

      {/* مختارات لكِ: منتجات مشابهة للي العميلة اتفرجت عليه (بيختفي لو مفيش تصفح) */}
      <RecommendedSection ids={recentIds} />

      <CategoryTiles categories={allCategories} images={categoryImages} />

      {deal && <DealOfTheDay product={deal} onExpire={() => setTimeout(fetchOffers, 1500)} />}

      {saleProducts.length > 1 && (
        <CategorySection eyebrow="وفّري أكثر" title="أقوى الخصومات" products={saleProducts.slice(1)} bgClass="" />
      )}

      <PromoBanners newCount={newCount} newDays={NEW_DAYS} saleCount={saleCount} />

      {categoriesLoading && (
        <>
          <CategorySectionSkeleton />
          <CategorySectionSkeleton bgClass="" />
        </>
      )}

      {!categoriesLoading && categoriesError && (
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <p className="font-cairo text-gray-600 mb-4">تعذّر تحميل المنتجات، تأكدي من الاتصال وحاولي مرة أخرى.</p>
            <Button
              variant="outline"
              onClick={fetchHomeCategories}
              className="border-burgundy-500 text-burgundy-500 hover:bg-burgundy-500 hover:text-white rounded-full font-cairo"
            >
              إعادة المحاولة
            </Button>
          </div>
        </section>
      )}

      {homeCategories.map((category, index) => (
        <CategorySection
          key={category.id}
          eyebrow="مختارات لكِ"
          title={category.name}
          products={category.products}
          categoryValue={category.id}
          bgClass={index % 2 === 0 ? 'bg-brand-white' : ''}
        />
      ))}

      <WhatsAppCta whatsappUrl={site.whatsapp_url} />
    </div>
  );
};

export default Home;
